import React, {useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useAuthState} from '../../services/firebase';
import {getAuth, signOut} from "firebase/auth";
import {useSidebar} from '../../context/SidebarContext';
import {useEdges} from '../../../features/edges/context/EdgesContext';
import {Dropdown} from 'react-bootstrap';

// Icons
import HomeRounded from "@mui/icons-material/HomeRounded";
import Activity from "@mui/icons-material/BarChartRounded";
import GearFill from "@mui/icons-material/SettingsRounded";
import Business from "@mui/icons-material/BusinessRounded";
import InfoCircle from "@mui/icons-material/InfoRounded";
import DoorOpen from "@mui/icons-material/LogoutRounded";
import Person from "@mui/icons-material/PersonRounded";
import Eyeglasses from "@mui/icons-material/ArticleRounded";

import './sidebar.css';
import {BuildRounded} from "@mui/icons-material/"; // We'll create this next
import ChangelogWidget from '../../../features/changelog/components/ChangelogWidget';

const Sidebar = () => {
  const {user} = useAuthState();
  const {edges} = useEdges();
  const auth = getAuth();
  const location = useLocation();
  const history = useHistory();
  const {sidebarContent, isCollapsed, toggleSidebar, isMobileOpen, toggleMobileSidebar} = useSidebar();

  const [showUserMenu, setShowUserMenu] = useState(false);

  // Determine current section (Home, Edge, Tenant)
  const isEdgeContext = location.pathname.startsWith('/edges/');
  const isTenantContext = location.pathname.startsWith('/tenants');

  // Extract Edge ID if in edge context
  const edgeId = isEdgeContext ? location.pathname.split('/')[2] : null;
  const currentEdge = edgeId ? edges.find((edge) => edge.id === edgeId) : null;
  const canManageEdge = !!currentEdge?.isAdmin;

  const navigateTo = (path) => {
    if (window.innerWidth <= 1000) {
      toggleMobileSidebar(false);
    }
    history.push(path);
  };

  const handleLogout = () => {
    signOut(auth);
  };

  if (!user) return null; // Don't show sidebar if not logged in

  return (
    <>
      <div onClick={() => toggleMobileSidebar(false)} />
      <div className={`sidebar-wrapper ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.svg" alt="SCC Cloud" />
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-section">
            <div className={`nav-item ${location.pathname === '/' ? 'active' : ''}`} onClick={() => navigateTo('/')}>
              <HomeRounded className="nav-icon" />
              <span className="nav-label">Home</span>
            </div>

            {isEdgeContext && edgeId && (
              <>
                <div className={`nav-item ${location.pathname.includes('/statistics') ? 'active' : ''}`}
                  onClick={() => navigateTo(`/edges/${edgeId}/statistics`)}>
                  <Activity className="nav-icon" />
                  <span className="nav-label">Statistics</span>
                </div>
                {canManageEdge && (
                  <>
                    <div className={`nav-item ${location.pathname.includes('/configuration') ? 'active' : ''}`}
                      onClick={() => navigateTo(`/edges/${edgeId}/configuration`)}>
                      <GearFill className="nav-icon" />
                      <span className="nav-label">Configuration</span>
                    </div>
                    <div className={`nav-item ${location.pathname.includes('/documentation') ? 'active' : ''}`}
                      onClick={() => navigateTo(`/edges/${edgeId}/documentation`)}>
                      <Eyeglasses className="nav-icon" />
                      <span className="nav-label">Documentation</span>
                    </div>
                    <div className={`nav-item ${location.pathname.includes('/settings') ? 'active' : ''}`}
                      onClick={() => navigateTo(`/edges/${edgeId}/settings`)}>
                      <BuildRounded className="nav-icon" />
                      <span className="nav-label">Settings</span>
                    </div>
                  </>
                )}
              </>
            )}

            {isTenantContext && (
              <div className={`nav-item ${location.pathname === '/tenants' ? 'active' : ''}`}
                onClick={() => navigateTo('/tenants')}>
                <Business className="nav-icon" />
                <span className="nav-label">Tenants</span>
              </div>
            )}
          </div>

          {/* Dynamic Content Area (e.g. Stats Tree) */}
          {sidebarContent && (
            <div className="sidebar-dynamic-content">
              {sidebarContent}
            </div>
          )}

          <div className="sidebar-changelog">
            {location.pathname === '/' && <ChangelogWidget />}
          </div>
        </div>

        <div className="sidebar-footer">

          <a
            href="https://lehmannch.atlassian.net/wiki/spaces/SCCKH"
            target="_blank"
            rel="noopener noreferrer"
            className="nav-item"
          >
            <InfoCircle className="nav-icon" />
            <span className="nav-label">Help & Support</span>
          </a>

          <Dropdown drop="up" className="user-dropdown">
            <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
              <div className="user-profile-btn">
                <div className="user-avatar">
                  <Person />
                </div>
                <div className="user-info">
                  <span className="user-name">{user.displayName || user.email}</span>
                </div>
              </div>
            </Dropdown.Toggle>

            <Dropdown.Menu className="user-dropdown-menu">
              <Dropdown.Item onClick={() => navigateTo('/tenants')}>My Tenant</Dropdown.Item>
              <Dropdown.Divider />
              <Dropdown.Item onClick={handleLogout} className="text-danger">
                <DoorOpen className="me-2" size={16} />
                Logout
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </>
  );
};

const CustomToggle = React.forwardRef(({children, onClick}, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    style={{cursor: 'pointer', width: '100%'}}
  >
    {children}
  </div>
));

export default Sidebar;
